/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../globalScope/GlobalScope';
import LoggerFactory from '../logger/LoggerFactory';
import IPeerConnection from './IPeerConnection';
import {ILogger} from '../logger/LoggerInterface';
import FeatureEnablement from '../environment/FeatureEnablement';
import {ILegacyRTCStatsReport} from './RtcConnectionMonitor';

class VanillaPeerConnection implements IPeerConnection {
  private readonly _logger: ILogger = LoggerFactory.getLogger('VanillaPeerConnection');
  private _peerConnection: RTCPeerConnection | null;

  constructor(configuration?: RTCConfiguration) {
    if (FeatureEnablement.webkitRTCPeerConnectionEnabled) {
      // Ignored as legacy webkitRTCPeerConnection class is deprecated
      // This was a Chrome specific implementation
      // Requires a ts-ignore directive to prevent typescript errors
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._peerConnection = new webkitRTCPeerConnection(configuration);

      return;
    }

    this._peerConnection = new GlobalScope.value.RTCPeerConnection(configuration);
  }

  get native(): RTCPeerConnection | null {
    return this._peerConnection;
  }

  get currentLocalDescription(): RTCSessionDescription | null {
    return this._peerConnection?.currentLocalDescription || null;
  }

  get currentRemoteDescription(): RTCSessionDescription | null {
    return this._peerConnection?.currentRemoteDescription || null;
  }

  get connectionState(): RTCPeerConnectionState | null {
    return this._peerConnection?.connectionState || null;
  }

  get iceConnectionState(): RTCIceConnectionState | null {
    return this._peerConnection?.iceConnectionState || null;
  }

  get supportsGetConfiguration(): boolean {
    return typeof this._peerConnection?.getConfiguration === 'function';
  }

  get supportsSetConfiguration(): boolean {
    return typeof this._peerConnection?.setConfiguration === 'function';
  }

  get supportsGetTransceivers(): boolean {
    return !FeatureEnablement.getTransceiversDisabled && typeof this._peerConnection?.getTransceivers === 'function';
  }

  get supportsAddTransceiver(): boolean {
    return !FeatureEnablement.addTranceiverDisabled && typeof this._peerConnection?.addTransceiver === 'function';
  }

  async createOffer(options?: RTCOfferOptions): Promise<RTCSessionDescriptionInit> {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    if (FeatureEnablement.promiseBasedPCMethodsDisabled) {
      // Ignored as legacy createOffer method with callbacks is deprecated
      // Requires a ts-ignore directive to prevent typescript errors
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return new Promise((resolve, reject) => this._peerConnection.createOffer(resolve, reject, options));
    }

    return this._peerConnection.createOffer(options);
  }

  async createAnswer(options?: RTCAnswerOptions): Promise<RTCSessionDescriptionInit> {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    if (FeatureEnablement.promiseBasedPCMethodsDisabled) {
      // Ignored as legacy createAnswer method with callbacks is deprecated
      // Requires a ts-ignore directive to prevent typescript errors
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return new Promise((resolve, reject) => this._peerConnection.createAnswer(resolve, reject, options));
    }

    return this._peerConnection.createAnswer(options);
  }

  async setLocalDescription(description: RTCSessionDescriptionInit): Promise<void> {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.setLocalDescription(FeatureEnablement.promiseBasedPCMethodsDisabled ?
      new RTCSessionDescription(description) : description);
  }

  async setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void> {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    const hasCrypto = description.sdp?.match(/a=crypto:/i);

    if (hasCrypto) {
      this._logger.warn('SDP a=crypto is not supported');
    }

    return this._peerConnection.setRemoteDescription(FeatureEnablement.promiseBasedPCMethodsDisabled ?
      new RTCSessionDescription(description) : description);
  }

  getSenders(): RTCRtpSender[] {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.getSenders();
  }

  getReceivers(): RTCRtpReceiver[] {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.getReceivers();
  }

  getStats(selector?: MediaStreamTrack | null): Promise<RTCStatsReport> {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.getStats(selector);
  }

  getStatsLegacy(): Promise<ILegacyRTCStatsReport> {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return new Promise(resolve => {
      // Ignored due to typescript not having legacy 'getStats' method type definition
      // This was a Chrome specific implementation of the 'getStats' method for
      // Chrome@<= 66 detailed here: https://bugs.chromium.org/p/chromium/issues/detail?id=627816
      // Requires a ts-ignore directive to prevent typescript errors
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const ignored = this._peerConnection.getStats(resolve);
    });
  }

  addEventListener<T extends keyof RTCPeerConnectionEventMap>(type: T, listener: (this: RTCPeerConnection, event: RTCPeerConnectionEventMap[T]) => unknown, options?: boolean | AddEventListenerOptions): void {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.addEventListener(type, listener, options);
  }

  removeEventListener<T extends keyof RTCPeerConnectionEventMap>(type: T, listener: (this: RTCPeerConnection, event: RTCPeerConnectionEventMap[T]) => unknown, options?: boolean | AddEventListenerOptions): void {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.removeEventListener(type, listener, options);
  }

  getTransceivers(): RTCRtpTransceiver[] {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.getTransceivers();
  }

  addTransceiver(name: string, options: RTCRtpTransceiverInit): RTCRtpTransceiver {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.addTransceiver(name, options);
  }

  addStream(mediaStream: MediaStream): void {
    mediaStream.getTracks().forEach(track => {
      if (!this._peerConnection) {
        throw new Error('No PeerConnection initialized');
      }

      this._peerConnection.addTrack(track);
    });
  }

  getConfiguration(): RTCConfiguration {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.getConfiguration();
  }

  setConfiguration(configuration: RTCConfiguration): void {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.setConfiguration(configuration);
  }

  set ontrack(handler: ((this: RTCPeerConnection, event: RTCTrackEvent) => unknown) | null) {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.ontrack = handler;
  }

  get ontrack(): ((this: RTCPeerConnection, event: RTCTrackEvent) => unknown) | null {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.ontrack;
  }

  set onicecandidate(handler: ((this: RTCPeerConnection, event: RTCPeerConnectionIceEvent) => unknown) | null) {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.onicecandidate = handler;
  }

  get onicecandidate(): ((this: RTCPeerConnection, event: RTCPeerConnectionIceEvent) => unknown) | null {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.onicecandidate;
  }

  set onconnectionstatechange(handler: ((this: RTCPeerConnection, event: Event) => unknown) | null) {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.onconnectionstatechange = handler;
  }

  get onconnectionstatechange(): ((this: RTCPeerConnection, event: Event) => unknown) | null {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.onconnectionstatechange;
  }

  set oniceconnectionstatechange(handler: ((this: RTCPeerConnection, event: Event) => unknown) | null) {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    this._peerConnection.oniceconnectionstatechange = handler;
  }

  get oniceconnectionstatechange(): ((this: RTCPeerConnection, event: Event) => unknown) | null {
    if (!this._peerConnection) {
      throw new Error('No PeerConnection initialized');
    }

    return this._peerConnection.oniceconnectionstatechange;
  }

  close(): void {
    this._peerConnection?.close();
  }

  dispose(): void {
    this._peerConnection = null;
  }
}

export default VanillaPeerConnection;